import { accountingApi } from '../../../../config/apiClient';
import { ACCOUNTING_API } from '../../../../config/api';

// Kategori tipi
export interface InventoryCategoryDto {
  id: string;
  companyId: string;
  name: string;
  parentId: string | null;
  parentName?: string;
  level: number;
  isEcommerceEnabled: boolean;
  imageUrl?: string;
  headerImageUrl?: string;
  orderIndex: number;
  description?: string;
  isActive: boolean;
  createdAt: string;
  children?: InventoryCategoryDto[];
  itemCount: number;
}

export interface CreateInventoryCategoryDto {
  companyId: string;
  name: string;
  parentId?: string | null;
  isEcommerceEnabled?: boolean;
  imageUrl?: string;
  headerImageUrl?: string;
  orderIndex?: number;
  description?: string;
  isActive?: boolean;
}

export interface UpdateInventoryCategoryDto {
  companyId: string; // ✅ MULTI-TENANT: Şirket ID'si
  id: string;
  name: string;
  parentId?: string | null;
  isEcommerceEnabled: boolean;
  imageUrl?: string;
  headerImageUrl?: string;
  orderIndex: number;
  description?: string;
  isActive: boolean;
}

// Kategori servis fonksiyonları
export class CategoryService {
  /**
   * Şirkete ait tüm kategorileri getir
   */
  static async getAllCategories(companyId: string): Promise<InventoryCategoryDto[]> {
    try {
      console.log('🔍 [CATEGORY SERVICE] Kategoriler yükleniyor:', companyId);
      const response = await accountingApi.get<InventoryCategoryDto[]>(
        ACCOUNTING_API.INVENTORY_CATEGORIES
      );
      console.log('✅ [CATEGORY SERVICE] Kategoriler başarıyla yüklendi:', response.data.length);
      return response.data;
    } catch (error: any) {
      console.error('❌ [CATEGORY SERVICE] Kategori yükleme hatası:', error);
      throw new Error(error.response?.data?.message || 'Kategoriler yüklenirken hata oluştu');
    }
  }

  /**
   * Yeni kategori oluştur
   */
  static async createCategory(category: CreateInventoryCategoryDto): Promise<InventoryCategoryDto> {
    try {
      console.log('🔍 [CATEGORY SERVICE] Yeni kategori oluşturuluyor:', category);
      const response = await accountingApi.post<InventoryCategoryDto>(
        ACCOUNTING_API.INVENTORY_CATEGORY_CREATE,
        category
      );
      console.log('✅ [CATEGORY SERVICE] Kategori başarıyla oluşturuldu:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [CATEGORY SERVICE] Kategori oluşturma hatası:', error);
      throw new Error(error.response?.data?.message || 'Kategori oluşturulurken hata oluştu');
    }
  }

  /**
   * Kategoriyi güncelle - POST kullanıyoruz (sunucu kısıtlaması)
   */
  static async updateCategory(category: UpdateInventoryCategoryDto): Promise<InventoryCategoryDto> {
    try {
      const url = ACCOUNTING_API.INVENTORY_CATEGORY_UPDATE(category.id);
      console.log('🔍 [CATEGORY SERVICE] Kategori güncelleniyor:', category);
      console.log('🔍 [CATEGORY SERVICE] API URL:', url);
      
      const response = await accountingApi.post<InventoryCategoryDto>(url, category);
      
      console.log('✅ [CATEGORY SERVICE] Kategori başarıyla güncellendi:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [CATEGORY SERVICE] Kategori güncelleme hatası:', error);
      console.error('❌ [CATEGORY SERVICE] Error response:', error.response?.data);
      console.error('❌ [CATEGORY SERVICE] Error status:', error.response?.status);
      throw new Error(error.response?.data?.message || error.response?.data?.error || 'Kategori güncellenirken hata oluştu');
    }
  }

  /**
   * Kategoriyi sil - POST kullanıyoruz (sunucu kısıtlaması)
   */
  static async deleteCategory(companyId: string, categoryId: string): Promise<void> {
    try {
      console.log('🔍 [CATEGORY SERVICE] Kategori siliniyor:', categoryId);
      console.log('🔍 [CATEGORY SERVICE] API URL:', ACCOUNTING_API.INVENTORY_CATEGORY_DELETE(categoryId));
      
      const response = await accountingApi.post(
        ACCOUNTING_API.INVENTORY_CATEGORY_DELETE(categoryId)
      );
      
      console.log('✅ [CATEGORY SERVICE] API yanıtı:', response);
      console.log('✅ [CATEGORY SERVICE] Status:', response.status);
      console.log('✅ [CATEGORY SERVICE] Data:', response.data);
      console.log('✅ [CATEGORY SERVICE] Kategori başarıyla silindi');
    } catch (error: any) {
      console.error('❌ [CATEGORY SERVICE] Kategori silme hatası:', error);
      console.error('❌ [CATEGORY SERVICE] Error response:', error.response);
      console.error('❌ [CATEGORY SERVICE] Error message:', error.message);
      throw new Error(error.response?.data?.message || 'Kategori silinirken hata oluştu');
    }
  }

  /**
   * Kategori ID'sine göre kategori getir
   */
  static async getCategoryById(companyId: string, categoryId: string): Promise<InventoryCategoryDto> {
    try {
      console.log('🔍 [CATEGORY SERVICE] Kategori getiriliyor:', categoryId);
      const response = await accountingApi.get<InventoryCategoryDto>(
        ACCOUNTING_API.INVENTORY_CATEGORY_BY_ID(categoryId)
      );
      console.log('✅ [CATEGORY SERVICE] Kategori başarıyla getirildi:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [CATEGORY SERVICE] Kategori getirme hatası:', error);
      throw new Error(error.response?.data?.message || 'Kategori getirilirken hata oluştu');
    }
  }

  /**
   * Kategori e-ticaret durumunu değiştir - POST kullanıyoruz (sunucu kısıtlaması)
   */
  static async toggleEcommerceStatus(companyId: string, categoryId: string, enable: boolean): Promise<void> {
    try {
      console.log('🔍 [CATEGORY SERVICE] Kategori e-ticaret durumu değiştiriliyor:', { categoryId, enable });
      await accountingApi.post(
        ACCOUNTING_API.INVENTORY_CATEGORY_TOGGLE_ECOMMERCE(categoryId, enable)
      );
      console.log('✅ [CATEGORY SERVICE] Kategori e-ticaret durumu başarıyla değiştirildi');
    } catch (error: any) {
      console.error('❌ [CATEGORY SERVICE] E-ticaret durumu değiştirme hatası:', error);
      throw new Error(error.response?.data?.message || 'E-ticaret durumu değiştirilirken hata oluştu');
    }
  }
}

